/**
 * Course Detail Page - Course overview, curriculum, and enrollment
 */
import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Navigation } from '../../components/layout/Navigation';
import { Footer } from '../../components/layout/Footer';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { useCurrentUser } from '../../contexts/AuthContext';
import { api } from '../../services/api/endpoints';
import { paymentsApi } from '../../services/api/payments';
import type { Course, Lesson } from '../../types/models';
import { getPage } from '../../service/app/app.service';
import {
  getOrCreateStudentToken,
  hasCourseAccess,
  markCoursePurchased,
} from '../../services/billing/local-billing';
import './course-detail.css';

type BootcampApiItem = Readonly<{
  isCourseReady?: boolean;
  is_course_ready?: boolean;
  modulesZip?: string;
  modulesZipUrl?: string;
  modules_zip?: string;
  modules_zip_url?: string;
  cheatsheetPreview?: string;
  cheatsheetPreviewUrl?: string;
  cheatsheet_preview?: string;
  cheatsheet_preview_url?: string;
  cheatSheetPreview?: string;
  cheatSheetPreviewUrl?: string;
  cheat_sheet_preview?: string;
  cheat_sheet_preview_url?: string;
  cheatsheet?: string;
  cheatsheetUrl?: string;
  cheatsheet_url?: string;
  cheatSheet?: string;
  cheatSheetUrl?: string;
  cheat_sheet?: string;
  cheat_sheet_url?: string;
  modules?: ReadonlyArray<
    Readonly<{
      title?: string;
      thumbnail?: string;
      videoUrl?: string;
      video_url?: string;
    }>
  >;
  id: string;
  title: string;
  description: string;
  shortDescription?: string;
  thumbnail: string;
  trailerUrl?: string;
  trailer_url?: string;
  videoUrl?: string;
  video_url?: string;
  paymentLink?: string;
  payment_link?: string;
  stripePaymentLink?: string;
  stripe_payment_link?: string;
  checkoutUrl?: string;
  checkout_url?: string;
  instructor?: Readonly<{
    profileImg?: string;
    name?: string;
    email?: string;
  }>;
  level?: string;
  language?: string;
  topics?: ReadonlyArray<string>;
  curriculum?: ReadonlyArray<string>;
  circulum?: ReadonlyArray<string>;
  duration?: number;
  lessonsCount?: number;
  studentsCount?: number;
  rating?: number;
  reviewsCount?: number;
  isFree?: boolean;
  isPublished?: boolean;
  createdAt?: string;
  updatedAt?: string;
}>;

type CourseModule = Readonly<{
  title: string;
  thumbnail?: string;
  videoUrl: string;
}>;

type ZipEntry = Readonly<{
  filename: string;
  data: Uint8Array;
  crc32: number;
  lastModified: Date;
}>;

type CourseDetailData = Course & Readonly<{
  curriculum: ReadonlyArray<string>;
  modules: ReadonlyArray<CourseModule>;
  modulesZipUrl?: string;
  cheatsheetPreviewUrl?: string;
  cheatsheetDownloadUrl?: string;
  isCourseReady: boolean;
  instructorEmail?: string;
}>;

type BootcampPageData = Readonly<{
  bootcamps?: ReadonlyArray<BootcampApiItem>;
  data?: Readonly<{
    bootcamps?: ReadonlyArray<BootcampApiItem>;
  }>;
}>;

const REGISTRATION_UNLOCK_COURSE_ID = 'course-intro';
const ACCESS_CODE_QUERY_KEYS = ['access_code', 'accessCode', 'code'] as const;

const getAccessCodeFromSearch = (search: string): string => {
  const params = new URLSearchParams(search);
  for (const key of ACCESS_CODE_QUERY_KEYS) {
    const value = params.get(key)?.trim();
    if (value) {
      return value.toUpperCase();
    }
  }
  return '';
};

const removeAccessCodeFromSearch = (search: string): string => {
  const params = new URLSearchParams(search);
  for (const key of ACCESS_CODE_QUERY_KEYS) {
    params.delete(key);
  }
  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : '';
};

const vertexShaderSource = `
  attribute vec2 aPosition;
  varying vec2 vUv;
  void main() {
    vUv = (aPosition + 1.0) * 0.5;
    gl_Position = vec4(aPosition, 0.0, 1.0);
  }
`;

const fragmentShaderSource = `
  precision mediump float;
  uniform float uTime;
  uniform vec2 uResolution;
  varying vec2 vUv;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }

  void main() {
    vec2 uv = vUv;
    vec2 p = (uv - 0.5) * vec2(uResolution.x / max(uResolution.y, 1.0), 1.0);
    float t = uTime * 0.2;

    float waves = sin((p.x * 10.0) + (t * 2.8)) + cos((p.y * 8.0) - (t * 2.2));
    float swirl = sin(length(p) * 16.0 - t * 4.8);
    float cloud = noise((p * 3.0) + vec2(t * 0.8, -t * 0.6));
    float halo = smoothstep(1.0, 0.0, length(p * vec2(1.25, 1.0)));

    vec3 base = vec3(0.03, 0.07, 0.15);
    vec3 sky = vec3(0.48, 0.86, 1.0);
    vec3 sun = vec3(0.97, 0.79, 0.4);

    vec3 color = base;
    color += sky * smoothstep(-0.6, 1.0, waves + cloud * 0.45) * 0.28;
    color += sun * smoothstep(-0.4, 1.0, swirl + cloud * 0.52) * 0.26;
    color += vec3(0.06, 0.16, 0.28) * halo * 0.6;
    color += vec3(noise((uv * 8.0) + t) * 0.03);

    gl_FragColor = vec4(color, 0.82);
  }
`;

const createShader = (
  gl: WebGLRenderingContext,
  shaderType: number,
  source: string
): WebGLShader | null => {
  const shader = gl.createShader(shaderType);
  if (!shader) {
    return null;
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
};

const createProgram = (
  gl: WebGLRenderingContext,
  vertexShader: WebGLShader,
  fragmentShader: WebGLShader
): WebGLProgram | null => {
  const program = gl.createProgram();
  if (!program) {
    return null;
  }
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }
  return program;
};

const DetailAtmosphereCanvas: React.FC = () => {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance',
    });
    if (!gl) {
      return undefined;
    }

    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
    if (!vertexShader || !fragmentShader) {
      return undefined;
    }

    const program = createProgram(gl, vertexShader, fragmentShader);
    if (!program) {
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
      return undefined;
    }

    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    const buffer = gl.createBuffer();
    if (!buffer) {
      gl.deleteProgram(program);
      return undefined;
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW
    );

    const positionLocation = gl.getAttribLocation(program, 'aPosition');
    const timeLocation = gl.getUniformLocation(program, 'uTime');
    const resolutionLocation = gl.getUniformLocation(program, 'uResolution');
    const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.floor(canvas.clientWidth * dpr);
      const height = Math.floor(canvas.clientHeight * dpr);
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
    };

    let frame = 0;
    const start = performance.now();

    const render = (now: number) => {
      resize();
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

      if (timeLocation) {
        gl.uniform1f(timeLocation, (now - start) / 1000);
      }
      if (resolutionLocation) {
        gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      }

      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };

    const loop = (now: number) => {
      render(now);
      frame = window.requestAnimationFrame(loop);
    };

    resize();
    if (reduceMotionQuery.matches) {
      render(start);
    } else {
      frame = window.requestAnimationFrame(loop);
    }

    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      window.cancelAnimationFrame(frame);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
    };
  }, []);

  return <canvas ref={canvasRef} className="h-full w-full" aria-hidden="true" />;
};

const isCourseLevel = (value?: string): value is Course['level'] =>
  value === 'beginner' || value === 'intermediate' || value === 'advanced' || value === 'all-levels';

const CURRICULUM_KEYS = new Set([
  'curriculum',
  'circulum',
  'crriculum',
  'cirriculum',
  'curriculumjson',
  'circulumjson',
  'coursecurriculum',
  'coursecirculum',
  'syllabus',
]);

const normalizeKey = (value: string): string =>
  value.toLowerCase().replace(/[^a-z]/g, '');

const parseCurriculumValue = (value: unknown): ReadonlyArray<string> => {
  if (Array.isArray(value)) {
    const stringEntries = value
      .filter((entry): entry is string => typeof entry === 'string')
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (stringEntries.length > 0) {
      return stringEntries;
    }

    const objectEntries = value
      .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
      .map((entry) => {
        const title = entry.title;
        if (typeof title === 'string' && title.trim()) {
          return title.trim();
        }
        const name = entry.name;
        if (typeof name === 'string' && name.trim()) {
          return name.trim();
        }
        const text = entry.text;
        if (typeof text === 'string' && text.trim()) {
          return text.trim();
        }
        return null;
      })
      .filter((entry): entry is string => Boolean(entry));

    return objectEntries;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (Array.isArray(parsed)) {
        return parseCurriculumValue(parsed);
      }
    } catch {
      // If not JSON, continue with text split fallback.
    }

    return trimmed
      .split(/\r?\n|•|;|,/)
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return [];
};

const findCurriculumCandidate = (value: unknown, depth = 0): unknown => {
  if (!value || typeof value !== 'object' || depth > 4) {
    return null;
  }

  const obj = value as Record<string, unknown>;
  for (const [key, entry] of Object.entries(obj)) {
    if (CURRICULUM_KEYS.has(normalizeKey(key))) {
      return entry;
    }
  }

  for (const entry of Object.values(obj)) {
    const nested = findCurriculumCandidate(entry, depth + 1);
    if (nested != null) {
      return nested;
    }
  }

  return null;
};

const toCurriculumItems = (item: BootcampApiItem): ReadonlyArray<string> => {
  const dynamicItem = item as Record<string, unknown>;
  const candidates: ReadonlyArray<unknown> = [
    item.circulum,
    item.curriculum,
    dynamicItem.circulum_json,
    dynamicItem.curriculum_json,
    dynamicItem.courseCirculum,
    dynamicItem.courseCurriculum,
    dynamicItem.syllabus,
    findCurriculumCandidate(dynamicItem),
  ];

  for (const candidate of candidates) {
    const parsed = parseCurriculumValue(candidate);
    if (parsed.length) {
      return parsed;
    }
  }

  return [];
};

const toCourseModules = (item: BootcampApiItem): ReadonlyArray<CourseModule> => {
  if (!Array.isArray(item.modules)) {
    return [];
  }

  return item.modules
    .map((module, index) => {
      const rawVideoUrl = module.videoUrl || module.video_url;
      const videoUrl = typeof rawVideoUrl === 'string' ? rawVideoUrl.trim() : '';
      if (!videoUrl) {
        return null;
      }

      const rawTitle = typeof module.title === 'string' ? module.title.trim() : '';
      const rawThumbnail = typeof module.thumbnail === 'string' ? module.thumbnail.trim() : '';

      return {
        title: rawTitle || `Module ${String(index + 1).padStart(2, '0')}`,
        thumbnail: rawThumbnail || undefined,
        videoUrl,
      };
    })
    .filter((module): module is CourseModule => module !== null);
};

const pickFirstNonEmptyString = (candidates: ReadonlyArray<unknown>): string | undefined => {
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const normalized = candidate.trim();
      if (normalized) {
        return normalized;
      }
    }
  }
  return undefined;
};

const toCheatsheetDownloadUrl = (item: BootcampApiItem): string | undefined => {
  const dynamicItem = item as Record<string, unknown>;
  const candidates: ReadonlyArray<unknown> = [
    item.cheatsheet,
    item.cheatSheet,
    item.cheat_sheet,
    dynamicItem.cheatSheetPdf,
    dynamicItem.cheat_sheet_pdf,
    item.cheatsheetUrl,
    item.cheatsheet_url,
    item.cheatSheetUrl,
    item.cheat_sheet_url,
    dynamicItem.pdf,
  ];

  return pickFirstNonEmptyString(candidates);
};

const toCheatsheetPreviewUrl = (item: BootcampApiItem): string | undefined => {
  const dynamicItem = item as Record<string, unknown>;
  const candidates: ReadonlyArray<unknown> = [
    item.cheatsheetPreview,
    item.cheatsheetPreviewUrl,
    item.cheatsheet_preview,
    item.cheatsheet_preview_url,
    item.cheatSheetPreview,
    item.cheatSheetPreviewUrl,
    item.cheat_sheet_preview,
    item.cheat_sheet_preview_url,
    dynamicItem.preview,
    dynamicItem.previewUrl,
  ];

  return pickFirstNonEmptyString(candidates);
};

const toModulesZipUrl = (item: BootcampApiItem): string | undefined => {
  const dynamicItem = item as Record<string, unknown>;
  const candidates: ReadonlyArray<unknown> = [
    item.modulesZip,
    item.modulesZipUrl,
    item.modules_zip,
    item.modules_zip_url,
    dynamicItem.zip,
    dynamicItem.modules_zipfile,
  ];

  return pickFirstNonEmptyString(candidates);
};

const toCourse = (item: BootcampApiItem): CourseDetailData => {
  const instructorName = item.instructor?.name || 'Bootcamp Instructor';
  const instructorSlug = instructorName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const now = new Date().toISOString();
  const modules = toCourseModules(item);
  const trailerUrl = item.trailerUrl || item.trailer_url || item.videoUrl || item.video_url;
  const paymentLink =
    item.paymentLink ||
    item.payment_link ||
    item.stripePaymentLink ||
    item.stripe_payment_link ||
    item.checkoutUrl ||
    item.checkout_url;

  return {
    id: item.id,
    title: item.title,
    description: item.description,
    shortDescription: item.shortDescription || item.description,
    thumbnail: item.thumbnail,
    trailerUrl,
    paymentLink,
    instructor: {
      id: `inst-${instructorSlug}`,
      name: instructorName,
      title: 'Bootcamp Instructor',
      bio: `${instructorName} leads this coding bootcamp track.`,
      avatar:
        item.instructor?.profileImg ||
        'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200',
      expertise: item.topics || [],
      coursesCount: 1,
    },
    level: isCourseLevel(item.level) ? item.level : 'beginner',
    language: item.language || 'javascript',
    topics: item.topics || [],
    curriculum: toCurriculumItems(item),
    modules,
    modulesZipUrl: toModulesZipUrl(item),
    cheatsheetPreviewUrl: toCheatsheetPreviewUrl(item),
    cheatsheetDownloadUrl: toCheatsheetDownloadUrl(item),
    isCourseReady: item.isCourseReady ?? item.is_course_ready ?? true,
    instructorEmail: item.instructor?.email?.trim() || undefined,
    duration: item.duration || 0,
    lessonsCount: item.lessonsCount || modules.length,
    studentsCount: item.studentsCount || 0,
    rating: item.rating || 0,
    reviewsCount: item.reviewsCount || 0,
    isFree: item.isFree ?? false,
    isPublished: item.isPublished ?? true,
    createdAt: item.createdAt || now,
    updatedAt: item.updatedAt || now,
  };
};

const formatDurationHours = (minutes: number): string => {
  if (minutes <= 0) return '0h';
  return `${Math.max(1, Math.round(minutes / 60))}h`;
};

const formatLevel = (level: string): string =>
  level
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const toSlug = (value: string, fallback: string): string => {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
};

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let crc = index;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc & 1) === 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
    }
    table[index] = crc >>> 0;
  }
  return table;
})();

const calculateCrc32 = (bytes: Uint8Array): number => {
  let crc = 0xffffffff;
  for (let index = 0; index < bytes.length; index += 1) {
    crc = CRC32_TABLE[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const toDosDateTime = (
  value: Date
): Readonly<{
  dosDate: number;
  dosTime: number;
}> => {
  const year = Math.max(1980, value.getFullYear());
  const month = value.getMonth() + 1;
  const day = value.getDate();
  const hours = value.getHours();
  const minutes = value.getMinutes();
  const seconds = Math.floor(value.getSeconds() / 2);

  const dosDate = ((year - 1980) << 9) | (month << 5) | day;
  const dosTime = (hours << 11) | (minutes << 5) | seconds;

  return { dosDate, dosTime };
};

const createStoredZip = (entries: ReadonlyArray<ZipEntry>): Blob => {
  const encoder = new TextEncoder();
  const parts: BlobPart[] = [];
  const centralDirectoryParts: Uint8Array[] = [];
  let offset = 0;

  const writeUint16 = (view: DataView, position: number, value: number) => {
    view.setUint16(position, value & 0xffff, true);
  };

  const writeUint32 = (view: DataView, position: number, value: number) => {
    view.setUint32(position, value >>> 0, true);
  };

  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.filename);
    const { dosDate, dosTime } = toDosDateTime(entry.lastModified);
    const localHeader = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(localHeader.buffer);

    writeUint32(localView, 0, 0x04034b50);
    writeUint16(localView, 4, 20);
    writeUint16(localView, 6, 0);
    writeUint16(localView, 8, 0);
    writeUint16(localView, 10, dosTime);
    writeUint16(localView, 12, dosDate);
    writeUint32(localView, 14, entry.crc32);
    writeUint32(localView, 18, entry.data.length);
    writeUint32(localView, 22, entry.data.length);
    writeUint16(localView, 26, nameBytes.length);
    writeUint16(localView, 28, 0);
    localHeader.set(nameBytes, 30);

    parts.push(localHeader, entry.data);

    const centralHeader = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    writeUint32(centralView, 0, 0x02014b50);
    writeUint16(centralView, 4, 20);
    writeUint16(centralView, 6, 20);
    writeUint16(centralView, 8, 0);
    writeUint16(centralView, 10, 0);
    writeUint16(centralView, 12, dosTime);
    writeUint16(centralView, 14, dosDate);
    writeUint32(centralView, 16, entry.crc32);
    writeUint32(centralView, 20, entry.data.length);
    writeUint32(centralView, 24, entry.data.length);
    writeUint16(centralView, 28, nameBytes.length);
    writeUint16(centralView, 30, 0);
    writeUint16(centralView, 32, 0);
    writeUint16(centralView, 34, 0);
    writeUint16(centralView, 36, 0);
    writeUint32(centralView, 38, 0);
    writeUint32(centralView, 42, offset);
    centralHeader.set(nameBytes, 46);
    centralDirectoryParts.push(centralHeader);

    offset += localHeader.length + entry.data.length;
  }

  const centralDirectoryOffset = offset;
  let centralDirectorySize = 0;
  for (const part of centralDirectoryParts) {
    parts.push(part);
    centralDirectorySize += part.length;
  }

  const endOfCentralDirectory = new Uint8Array(22);
  const endView = new DataView(endOfCentralDirectory.buffer);
  writeUint32(endView, 0, 0x06054b50);
  writeUint16(endView, 4, 0);
  writeUint16(endView, 6, 0);
  writeUint16(endView, 8, entries.length);
  writeUint16(endView, 10, entries.length);
  writeUint32(endView, 12, centralDirectorySize);
  writeUint32(endView, 16, centralDirectoryOffset);
  writeUint16(endView, 20, 0);
  parts.push(endOfCentralDirectory);

  return new Blob(parts, { type: 'application/zip' });
};

/**
 * Course detail page
 */
export const CourseDetailPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const [activeTab, setActiveTab] = useState<'overview' | 'curriculum' | 'instructor'>('overview');
  const [checkoutPending, setCheckoutPending] = useState(false);
  const [accessCodeInput, setAccessCodeInput] = useState('');
  const [redeemPending, setRedeemPending] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const accessCodeInputRef = React.useRef<HTMLInputElement | null>(null);
  const autoRedeemAttemptedRef = React.useRef(false);
  const resolvedCourseId = decodeURIComponent(courseId || '').trim().toLowerCase();
  const isRegistrationUnlockCourse = resolvedCourseId === REGISTRATION_UNLOCK_COURSE_ID;
  const hasPaidCourseAccess = !!resolvedCourseId && hasCourseAccess(resolvedCourseId);
  const studentToken = getOrCreateStudentToken();

  const { data: course, isLoading: courseLoading } = useQuery({
    queryKey: ['course', courseId],
    queryFn: async () => {
      const result = await getPage<BootcampPageData>('bootcamp');
      if (result.error || !result.data) {
        throw new Error(
          typeof result.error === 'string' ? result.error : 'Failed to load bootcamp data'
        );
      }

      const rawBootcamps = result.data.bootcamps || result.data.data?.bootcamps || [];
      const found = rawBootcamps.find(
        (item) => item.id?.trim().toLowerCase() === resolvedCourseId
      );
      if (!found) {
        throw new Error('Course not found');
      }

      return toCourse(found);
    },
    enabled: !!resolvedCourseId,
  });

  const { data: lessons } = useQuery({
    queryKey: ['lessons', courseId],
    queryFn: async () => {
      const result = await api.lessons.getByCourse(decodeURIComponent(courseId!));
      if (result.success) {
        return result.data;
      }
      throw new Error(result.error.message);
    },
    enabled: !!courseId,
  });

  const { data: purchaseStatus } = useQuery({
    queryKey: ['course-purchase-status', resolvedCourseId, studentToken],
    queryFn: async () => {
      const result = await api.courses.purchaseStatus(resolvedCourseId, studentToken);
      if (result.success) {
        return result.data;
      }
      throw new Error(result.error.message);
    },
    enabled: !!resolvedCourseId && !hasPaidCourseAccess && !isRegistrationUnlockCourse,
    staleTime: 60_000,
  });

  const isFreeCourse = Boolean(course?.isFree);
  const remotelyPurchased = Boolean(purchaseStatus?.purchased);
  const canAccessCourse = hasPaidCourseAccess || remotelyPurchased || isFreeCourse;
  const isOwned = canAccessCourse;
  const lockedDownloadMessage = isRegistrationUnlockCourse
    ? 'Unlock this free course with the access code from your email first'
    : 'Purchase this course to download course assets';
  const isMyCourseDetailPage = location.pathname.startsWith('/my-courses/');
  const backToCatalogPath = isMyCourseDetailPage ? '/my-courses' : '/courses';
  const modulesZipUrl = course?.modulesZipUrl;
  const cheatsheetPreviewUrl = course?.cheatsheetPreviewUrl;
  const cheatsheetDownloadUrl = course?.cheatsheetDownloadUrl;
  const instructorContactEmail = course?.instructorEmail || 'clintonjohnson@cmjsoftware.com';

  const clearAccessCodeFromUrl = () => {
    const nextSearch = removeAccessCodeFromSearch(location.search);
    if (nextSearch === location.search) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch,
      },
      { replace: true }
    );
  };

  const redeemAccessCode = async (
    rawCode: string,
    options: Readonly<{ fromEmailLink?: boolean }> = {}
  ) => {
    const trimmedCode = rawCode.trim();
    if (!trimmedCode) {
      toast.error('Enter an access code first');
      return;
    }

    if (!resolvedCourseId) {
      toast.error('Missing course id');
      return;
    }

    setRedeemPending(true);
    const result = await paymentsApi.redeemAccessCode(trimmedCode, {
      courseId: resolvedCourseId,
      userId: currentUser?.id,
      email: currentUser?.email,
    });
    setRedeemPending(false);

    if (!result.success || !result.data.success || !result.data.access_granted) {
      toast.error(
        result.success
          ? result.data.reason || 'Failed to redeem code'
          : result.error.message
      );
      return;
    }

    markCoursePurchased(resolvedCourseId);
    setAccessCodeInput('');
    if (options.fromEmailLink) {
      clearAccessCodeFromUrl();
    }
    toast.success(
      isRegistrationUnlockCourse
        ? 'Intro to Software unlocked. Downloads are ready.'
        : 'Access code redeemed. Course unlocked.'
    );
  };

  React.useEffect(() => {
    if (!resolvedCourseId || hasPaidCourseAccess || !purchaseStatus?.purchased) {
      return;
    }

    markCoursePurchased(resolvedCourseId, purchaseStatus.checkoutSessionId ?? null);
  }, [resolvedCourseId, hasPaidCourseAccess, purchaseStatus]);

  React.useEffect(() => {
    if (
      !isRegistrationUnlockCourse ||
      !resolvedCourseId ||
      canAccessCourse ||
      autoRedeemAttemptedRef.current
    ) {
      return;
    }

    const accessCodeFromUrl = getAccessCodeFromSearch(location.search);
    if (!accessCodeFromUrl) {
      return;
    }

    autoRedeemAttemptedRef.current = true;
    setAccessCodeInput(accessCodeFromUrl);
    void redeemAccessCode(accessCodeFromUrl, { fromEmailLink: true });
  }, [canAccessCourse, isRegistrationUnlockCourse, location.search, resolvedCourseId]);

  const handleStartLesson = (lessonId: string) => {
    navigate(`/lessons/${lessonId}`);
  };

  const triggerBrowserDownload = (href: string, filename: string) => {
    if (href.startsWith('blob:')) {
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = filename;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const inferAssetExtension = (assetUrl: string, fallback: string): string => {
    try {
      const parsed = new URL(assetUrl);
      const pathname = parsed.pathname || '';
      const extension = pathname.split('.').pop()?.toLowerCase();
      if (extension && /^[a-z0-9]{2,5}$/.test(extension)) {
        return extension;
      }
    } catch {
      // Keep default extension below.
    }
    return fallback;
  };

  const downloadFile = async (url: string, filename: string): Promise<'blob' | 'direct'> => {
    try {
      const assetUrl = new URL(url, window.location.origin);
      if (assetUrl.origin !== window.location.origin) {
        triggerBrowserDownload(assetUrl.toString(), filename);
        return 'direct';
      }
    } catch {
      // If URL parsing fails, continue and let fetch/direct fallback handle it.
    }

    try {
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) {
        throw new Error(`Failed to fetch file (${response.status})`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      triggerBrowserDownload(blobUrl, filename);
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
      return 'blob';
    } catch {
      triggerBrowserDownload(url, filename);
      return 'direct';
    }
  };

  const handleDownloadModulesZip = async (modules: ReadonlyArray<CourseModule>) => {
    if (!course) {
      toast.error('Course unavailable');
      return;
    }

    if (!canAccessCourse) {
      toast.error(lockedDownloadMessage);
      return;
    }

    if (!modules.length) {
      toast.error('No modules available to download');
      return;
    }

    const courseSlug = toSlug(course.title, 'course');
    const zipFilename = `${courseSlug}-modules.zip`;

    if (modulesZipUrl) {
      triggerBrowserDownload(modulesZipUrl, zipFilename);
      toast.success('Started modules zip download.');
      return;
    }

    const canBuildZipInBrowser = modules.every((module) => {
      try {
        const moduleUrl = new URL(module.videoUrl, window.location.origin);
        return moduleUrl.origin === window.location.origin;
      } catch {
        return false;
      }
    });

    if (!canBuildZipInBrowser) {
      toast.error(
        'Modules ZIP is not available for this course yet. Add a modulesZip URL in bootcamp data to enable single-file download.'
      );
      return;
    }

    const loadingToastId = toast.loading('Preparing module zip...');

    try {
      const zipEntries: ZipEntry[] = [];
      for (let index = 0; index < modules.length; index += 1) {
        const module = modules[index];
        toast.loading(`Adding module ${index + 1} of ${modules.length}...`, {
          id: loadingToastId,
        });

        const response = await fetch(module.videoUrl, { mode: 'cors' });
        if (!response.ok) {
          throw new Error(`Unable to fetch module ${index + 1} (${response.status})`);
        }

        const data = new Uint8Array(await response.arrayBuffer());
        const extension = inferAssetExtension(module.videoUrl, 'mp4');
        const moduleSlug = toSlug(module.title, `module-${index + 1}`);
        const filename = `${String(index + 1).padStart(2, '0')}-${moduleSlug}.${extension}`;
        zipEntries.push({
          filename,
          data,
          crc32: calculateCrc32(data),
          lastModified: new Date(),
        });
      }

      const zipBlob = createStoredZip(zipEntries);
      const zipUrl = URL.createObjectURL(zipBlob);
      triggerBrowserDownload(zipUrl, zipFilename);
      window.setTimeout(() => URL.revokeObjectURL(zipUrl), 2000);
      toast.success('Started modules zip download.', { id: loadingToastId });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? `Failed to build modules zip: ${error.message}`
          : 'Failed to build modules zip.',
        { id: loadingToastId }
      );
    }
  };

  const handleDownloadCourse = async () => {
    if (!course) {
      toast.error('No course assets available to download');
      return;
    }

    if (course.modules.length > 0) {
      await handleDownloadModulesZip(course.modules);
      return;
    }

    const courseSlug = toSlug(course.title, 'course');
    const downloadableAssets: Array<
      Readonly<{ title: string; url: string; defaultExtension: string; fileStem: string }>
    > = [];

    if (lessons?.length) {
      downloadableAssets.push(
        ...lessons
          .filter((lesson: Lesson) => lesson.isFree || canAccessCourse)
          .map((lesson: Lesson, index: number) => ({
            title: lesson.title,
            url: lesson.videoUrl,
            defaultExtension: 'mp4',
            fileStem: `lesson-${String(index + 1).padStart(2, '0')}-${toSlug(
              lesson.title,
              `lesson-${index + 1}`
            )}`,
          }))
      );
    } else if (course.trailerUrl) {
      downloadableAssets.push({
        title: `${course.title} Trailer`,
        url: course.trailerUrl,
        defaultExtension: 'mp4',
        fileStem: 'trailer',
      });
    }

    if (cheatsheetDownloadUrl && canAccessCourse) {
      downloadableAssets.push({
        title: `${course.title} Cheatsheet`,
        url: cheatsheetDownloadUrl,
        defaultExtension: 'pdf',
        fileStem: 'cheatsheet',
      });
    }

    if (!downloadableAssets.length) {
      toast.error('You do not have download access for this course yet');
      return;
    }

    let blobDownloads = 0;
    let directDownloads = 0;

    for (let index = 0; index < downloadableAssets.length; index += 1) {
      const asset = downloadableAssets[index];
      const extension = inferAssetExtension(asset.url, asset.defaultExtension);
      const filename = `${courseSlug}-${asset.fileStem}.${extension}`;
      const mode = await downloadFile(asset.url, filename);
      if (mode === 'blob') {
        blobDownloads += 1;
      } else {
        directDownloads += 1;
      }

      await new Promise((resolve) => window.setTimeout(resolve, 250));
    }

    if (directDownloads > 0) {
      toast.success(
        `Started ${blobDownloads + directDownloads} download(s). Some opened in new tabs due to provider CORS.`
      );
      return;
    }

    toast.success(`Started ${blobDownloads} course asset download(s).`);
  };

  const handleDownloadModule = async (module: CourseModule, index: number) => {
    if (!canAccessCourse) {
      toast.error(lockedDownloadMessage);
      return;
    }

    if (!course) {
      toast.error('Course unavailable');
      return;
    }

    const courseSlug = toSlug(course.title, 'course');
    const moduleSlug = toSlug(module.title, `module-${index + 1}`);
    const extension = inferAssetExtension(module.videoUrl, 'mp4');
    const filename = `${courseSlug}-module-${String(index + 1).padStart(2, '0')}-${moduleSlug}.${extension}`;
    const mode = await downloadFile(module.videoUrl, filename);

    if (mode === 'direct') {
      toast.success('Started module download in a new tab.');
      return;
    }

    toast.success('Started module download.');
  };

  const handleDownloadCheatsheet = async () => {
    if (!cheatsheetDownloadUrl) {
      toast.error('No cheatsheet is available for this course');
      return;
    }

    if (!canAccessCourse) {
      toast.error(lockedDownloadMessage);
      return;
    }

    const courseSlug = toSlug(course.title, 'course');
    const extension = inferAssetExtension(cheatsheetDownloadUrl, 'pdf');
    const filename = `${courseSlug}-cheatsheet.${extension}`;
    const mode = await downloadFile(cheatsheetDownloadUrl, filename);

    if (mode === 'direct') {
      toast.success('Opened cheatsheet download in a new tab.');
      return;
    }

    toast.success('Started cheatsheet download.');
  };

  const handlePrimaryAction = async () => {
    if (isOwned) {
      await handleDownloadCourse();
      return;
    }

    if (!course?.isCourseReady) {
      toast('This course is coming soon.');
      return;
    }

    if (isRegistrationUnlockCourse) {
      if (accessCodeInput.trim()) {
        await handleRedeemAccessCode();
        return;
      }

      accessCodeInputRef.current?.focus();
      toast('Enter the access code from your email to unlock this free course.');
      return;
    }

    if (!course?.paymentLink) {
      toast.error('Payment link unavailable for this course');
      return;
    }

    const checkoutWindow = window.open('', '_blank');
    if (!checkoutWindow) {
      toast.error('Popup blocked. Please allow popups to open Stripe in a new tab.');
      return;
    }

    try {
      setCheckoutPending(true);

      const checkoutSession = await api.courses.createCheckoutSession(
        resolvedCourseId,
        course.paymentLink,
        studentToken
      );

      if (checkoutSession.success) {
        checkoutWindow.location.href = checkoutSession.data.url;
        return;
      }

      const fallbackUrl = new URL(course.paymentLink, window.location.origin);
      fallbackUrl.searchParams.set(
        'client_reference_id',
        `${resolvedCourseId}:${studentToken}`
      );
      if (currentUser?.email) {
        fallbackUrl.searchParams.set('prefilled_email', currentUser.email);
      }
      checkoutWindow.location.href = fallbackUrl.toString();
      toast.error('Managed checkout session unavailable. Falling back to direct Stripe link.');
    } catch (error) {
      checkoutWindow.close();
      toast.error(error instanceof Error ? error.message : 'Failed to open checkout');
    } finally {
      setCheckoutPending(false);
    }
  };

  const handleRedeemAccessCode = async () => {
    await redeemAccessCode(accessCodeInput);
  };

  if (courseLoading) {
    return (
      <div className="course-detail-page min-h-screen text-white">
        <Navigation />
        <div className="flex h-screen items-center justify-center">
          <Spinner size="xl" />
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="course-detail-page min-h-screen text-white">
        <Navigation />
        <Container className="pt-28">
          <div className="course-detail-panel rounded-2xl py-20 text-center">
            <p className="text-xl text-red-300">Course not found.</p>
            <Button className="mt-6" onClick={() => navigate('/courses')}>
              Browse Courses
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  const enrollmentBadge = isOwned
    ? 'Owned'
    : isRegistrationUnlockCourse
      ? 'Free Unlock'
      : 'Premium';
  const enrollmentTitle = isOwned
    ? 'You Own This Course'
    : isRegistrationUnlockCourse
      ? 'Unlock Free Course'
      : 'Get This Course';
  const enrollmentCopy = isOwned
    ? 'Access and download available course videos now.'
    : !course.isCourseReady
      ? 'This course is still in production and will be available soon.'
      : isRegistrationUnlockCourse
        ? 'Use the access code from your email to unlock and download this course.'
        : 'Checkout is secure and powered by Stripe.';
  const primaryActionLabel = isOwned
    ? 'Download Course Assets'
    : !course.isCourseReady
      ? 'Coming Soon'
      : isRegistrationUnlockCourse
        ? accessCodeInput.trim()
          ? redeemPending
            ? 'Unlocking...'
            : 'Unlock Free Course'
          : 'Enter Access Code'
        : checkoutPending
          ? 'Opening Checkout...'
          : 'Buy Now';

  return (
    <div className="course-detail-page min-h-screen text-white">
      <Navigation />

      <main className="relative overflow-hidden pb-20 pt-28 md:pt-32">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[560px] opacity-95">
          <DetailAtmosphereCanvas />
        </div>

        <Container>
          <section className="course-detail-panel rounded-[1.9rem] p-7 md:p-9">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="course-detail-pill rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-100">
                {formatLevel(course.level)}
              </span>
              <span className="course-detail-pill rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-100">
                {course.language}
              </span>
              <span className="course-detail-pill rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-100">
                {enrollmentBadge}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
              <div className="lg:col-span-3">
                <p className="text-xs uppercase tracking-[0.2em] text-cyan-100/85">Course Detail</p>
                <h1 className="course-detail-display mt-3 text-4xl font-bold md:text-6xl">
                  {course.title}
                </h1>
                <p className="mt-4 max-w-3xl text-lg text-slate-100/87">
                  {course.shortDescription}
                </p>

                <div className="mt-6 flex flex-wrap gap-4 text-sm text-slate-100/85">
                  <span>★ {course.rating.toFixed(1)} ({course.reviewsCount} reviews)</span>
                  <span>{course.studentsCount.toLocaleString()} students</span>
                  <span>
                    {course.modules.length > 0
                      ? `${course.modules.length} modules`
                      : `${course.lessonsCount} lessons`}
                  </span>
                  <span>{formatDurationHours(course.duration)} total</span>
                </div>

                <div className="mt-7 flex flex-wrap gap-3">
                  <Button variant="gold" size="md" onClick={() => setActiveTab('curriculum')}>
                    View Curriculum
                  </Button>
                  <Link to={backToCatalogPath}>
                    <Button variant="outline" size="md">
                      {isMyCourseDetailPage ? 'Back To My Courses' : 'Back To Courses'}
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="course-detail-pill rounded-2xl p-5">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-200">Instructor</p>
                  <div className="mt-4 flex items-center gap-3">
                    <img
                      src={course.instructor.avatar}
                      alt={course.instructor.name}
                      className="h-12 w-12 rounded-xl border border-white/35 object-cover"
                    />
                    <div>
                      <p className="font-semibold text-white">{course.instructor.name}</p>
                      <p className="text-sm text-slate-300/90">{course.instructor.title}</p>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-slate-200/90">{course.instructor.bio}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="space-y-8 lg:col-span-2">
              <div className="course-detail-panel overflow-hidden rounded-2xl">
                <div className="aspect-video bg-black">
                  {course.trailerUrl ? (
                    <video
                      className="h-full w-full object-cover"
                      src={course.trailerUrl}
                      controls
                      poster={course.thumbnail}
                      preload="metadata"
                    />
                  ) : (
                    <img
                      src={course.thumbnail}
                      alt={course.title}
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
              </div>

              {course.modules.length > 0 && (
                <div className="course-detail-panel rounded-2xl p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="course-detail-display text-2xl font-bold text-white">
                        Included Modules
                      </h3>
                      <p className="mt-1 text-sm text-slate-300/90">
                        These module videos are included with this course and can be downloaded.
                      </p>
                    </div>
                    <span className="course-detail-pill rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-100">
                      {course.modules.length} Modules
                    </span>
                  </div>

                  <div className="mt-5 space-y-3">
                    {course.modules.map((module, index) => (
                      <article
                        key={`detail-module-${index}-${module.videoUrl}`}
                        className="course-detail-lesson rounded-xl p-4"
                      >
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                          <img
                            src={module.thumbnail || course.thumbnail}
                            alt={module.title}
                            className="h-20 w-full rounded-lg border border-white/20 object-cover sm:w-36"
                          />
                          <div className="flex-1">
                            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-100/85">
                              Module {String(index + 1).padStart(2, '0')}
                            </span>
                            <p className="mt-1 text-sm text-slate-100">{module.title}</p>
                          </div>
                          <div className="flex items-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => void handleDownloadModule(module, index)}
                              disabled={!canAccessCourse}
                            >
                              {canAccessCourse ? 'Download' : 'Locked'}
                            </Button>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              )}

              <div className="course-detail-panel rounded-2xl p-6">
                <div className="mb-6 flex flex-wrap gap-2">
                  {(['overview', 'curriculum', 'instructor'] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveTab(tab)}
                      className={`course-detail-tab rounded-full px-4 py-2 text-sm font-semibold uppercase tracking-[0.14em] ${
                        activeTab === tab ? 'course-detail-tab-active' : ''
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {activeTab === 'overview' && (
                  <div>
                    <h3 className="course-detail-display text-3xl font-bold text-white">
                      About This Course
                    </h3>
                    <p className="mt-4 text-slate-200/92">{course.description}</p>
                    <h4 className="course-detail-display mt-8 text-2xl font-bold text-white">
                      What You'll Build
                    </h4>
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                      {course.topics.map((topic) => (
                        <div
                          key={topic}
                          className="course-detail-pill rounded-xl px-4 py-3 text-sm text-slate-100"
                        >
                          {topic}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'curriculum' && (
                  <div>
                    <h3 className="course-detail-display text-3xl font-bold text-white">
                      Course Curriculum
                    </h3>
                    {course.modules.length > 0 && (
                      <div className="mt-5 space-y-3">
                        {course.modules.map((module, index) => (
                          <article
                            key={`${index}-${module.videoUrl}`}
                            className="course-detail-lesson rounded-xl p-4"
                          >
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                              <img
                                src={module.thumbnail || course.thumbnail}
                                alt={module.title}
                                className="h-20 w-full rounded-lg border border-white/20 object-cover sm:w-36"
                              />
                              <div className="flex-1">
                                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-100/85">
                                  Module {String(index + 1).padStart(2, '0')}
                                </span>
                                <p className="mt-1 text-sm text-slate-100">{module.title}</p>
                              </div>
                              <div className="flex items-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => void handleDownloadModule(module, index)}
                                  disabled={!canAccessCourse}
                                >
                                  {canAccessCourse ? 'Download' : 'Locked'}
                                </Button>
                              </div>
                            </div>
                          </article>
                        ))}
                      </div>
                    )}
                    {course.curriculum.length > 0 ? (
                      <div className="mt-5 space-y-3">
                        {course.curriculum.map((item, index) => {
                          const linkedLesson = lessons?.[index];
                          const canStartLinkedLesson = Boolean(
                            linkedLesson && (linkedLesson.isFree || canAccessCourse)
                          );

                          return (
                            <article
                              key={`${index}-${item}`}
                              className="course-detail-lesson rounded-xl p-5"
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <div className="mb-2 flex flex-wrap items-center gap-3">
                                    <span className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-100/85">
                                      {String(index + 1).padStart(2, '0')}
                                    </span>
                                  </div>
                                  <p className="text-sm text-slate-200/90">{item}</p>
                                </div>
                                {canStartLinkedLesson && linkedLesson && (
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm text-slate-300">
                                      {Math.floor(linkedLesson.duration / 60)}:
                                      {String(linkedLesson.duration % 60).padStart(2, '0')}
                                    </span>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleStartLesson(linkedLesson.id)}
                                    >
                                      {linkedLesson.isFree ? 'Preview' : 'Start'}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    ) : (
                      course.modules.length === 0 && (
                        <p className="mt-4 text-slate-200/85">
                          Curriculum data was not found in the bootcamp payload yet.
                        </p>
                      )
                    )}
                  </div>
                )}

                {activeTab === 'instructor' && (
                  <div>
                    <div className="flex flex-col gap-5 md:flex-row md:items-start">
                      <img
                        src={course.instructor.avatar}
                        alt={course.instructor.name}
                        className="h-24 w-24 rounded-2xl border border-white/35 object-cover md:h-28 md:w-28"
                      />
                      <div>
                        <h3 className="course-detail-display text-3xl font-bold text-white">
                          {course.instructor.name}
                        </h3>
                        <p className="mt-1 text-slate-300">{course.instructor.title}</p>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {course.instructor.expertise.map((skill) => (
                            <span
                              key={skill}
                              className="course-detail-pill rounded-full px-3 py-1 text-xs font-semibold text-slate-100"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="mt-5 text-slate-200/90">{course.instructor.bio}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6 lg:col-span-1">
              <aside className="course-detail-panel sticky top-24 rounded-2xl p-6">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-cyan-100/82">
                    Enrollment
                  </p>
                  <h3 className="course-detail-display mt-2 text-3xl font-bold text-white">
                    {enrollmentTitle}
                  </h3>
                  <p className="mt-2 text-sm text-slate-300/90">
                    {enrollmentCopy}
                  </p>
                </div>

                <div className="mt-6">
                  <Button
                    variant="gold"
                    size="lg"
                    fullWidth
                    onClick={handlePrimaryAction}
                    disabled={checkoutPending || redeemPending || (!isOwned && !course.isCourseReady)}
                  >
                    {primaryActionLabel}
                  </Button>
                </div>

                {!isOwned && (
                  <div className="mt-6 border-t border-slate-500/35 pt-6">
                    <p className="mb-3 text-sm text-slate-300/88">
                      {isRegistrationUnlockCourse
                        ? 'Enter the code from your free intro email.'
                        : 'Have an access code?'}
                    </p>
                    <input
                      ref={accessCodeInputRef}
                      value={accessCodeInput}
                      onChange={(event) => setAccessCodeInput(event.target.value.toUpperCase())}
                      placeholder="ENTER-CODE-HERE"
                      className="course-detail-input mb-3"
                    />
                    <Button
                      variant="outline"
                      fullWidth
                      onClick={handleRedeemAccessCode}
                      disabled={redeemPending || !accessCodeInput.trim()}
                    >
                      {redeemPending
                        ? 'Redeeming...'
                        : isRegistrationUnlockCourse
                          ? 'Unlock Free Course'
                          : 'Redeem Code'}
                    </Button>
                    {isRegistrationUnlockCourse && (
                      <p className="mt-3 text-xs text-slate-300/80">
                        Need a code?{' '}
                        <Link
                          to="/app-registration"
                          className="font-semibold text-cyan-200 underline decoration-cyan-300/70 underline-offset-4"
                        >
                          Register for free access.
                        </Link>
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-6 border-t border-slate-500/35 pt-6">
                  <p className="mb-3 text-xs uppercase tracking-[0.18em] text-slate-300">Included</p>
                  <div className="space-y-2 text-sm text-slate-200/92">
                    <p>
                      {course.modules.length > 0
                        ? `${course.modules.length} downloadable modules`
                        : `${course.lessonsCount} structured lessons`}
                    </p>
                    <p>{formatDurationHours(course.duration)} of guided content</p>
                    <p>Device-friendly lesson access</p>
                    <p>Completion certificate</p>
                  </div>
                </div>
              </aside>

              {cheatsheetPreviewUrl && (
                <section className="course-detail-panel rounded-2xl p-6">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="course-detail-display text-xl font-bold text-white">
                      Student Cheatsheet (PDF)
                    </h4>
                    <span className="course-detail-pill rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-100">
                      {canAccessCourse ? 'Download Unlocked' : 'Preview Only'}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-slate-300/90">
                    A quick-reference guide for this course. Students can preview it here, and
                    downloads unlock after access is granted.
                  </p>
                  <div className="mt-4 overflow-hidden rounded-xl border border-white/20 bg-black/30">
                    <img
                      src={cheatsheetPreviewUrl}
                      alt={`${course.title} cheatsheet preview`}
                      className="h-72 w-full object-cover"
                    />
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handleDownloadCheatsheet()}
                      disabled={!canAccessCourse || !cheatsheetDownloadUrl}
                    >
                      {canAccessCourse
                        ? cheatsheetDownloadUrl
                          ? 'Download Cheatsheet'
                          : 'Download Unavailable'
                        : 'Download Locked'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setIsPreviewModalOpen(true)}
                    >
                      Open Preview
                    </Button>
                  </div>
                </section>
              )}

              <section className="course-detail-panel rounded-2xl p-6">
                <h4 className="course-detail-display text-xl font-bold text-white">
                  Student Support & Graduation
                </h4>
                <div className="mt-4 space-y-3">
                  <div className="rounded-xl border border-cyan-300/30 bg-cyan-300/10 p-3 text-sm text-slate-100">
                    Each student receives one free 1-hour session with their instructor after
                    enrollment, designed as a 1-on-1 space to ask questions, get clarity, and deepen
                    understanding where needed. Please email your instructor at
                    {' '}
                    <a
                      href={`mailto:${instructorContactEmail}`}
                      className="font-semibold text-cyan-200 underline decoration-cyan-300/70 underline-offset-4"
                    >
                      {instructorContactEmail}
                    </a>
                    {' '}
                    to schedule your 1-hour session if needed.
                  </div>
                  <div className="rounded-xl border border-amber-300/30 bg-amber-300/10 p-3 text-sm text-slate-100">
                    Upon graduation, students receive a certificate of completion via email. For
                    instructor support or follow-up questions, email
                    {' '}
                    <a
                      href={`mailto:${instructorContactEmail}`}
                      className="font-semibold text-amber-200 underline decoration-amber-300/70 underline-offset-4"
                    >
                      {instructorContactEmail}
                    </a>
                    .
                  </div>
                </div>
              </section>
            </div>
          </section>
        </Container>
      </main>

      {isPreviewModalOpen && cheatsheetPreviewUrl && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/85 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Cheatsheet preview"
          onClick={() => setIsPreviewModalOpen(false)}
        >
          <div
            className="course-detail-panel w-full max-w-5xl rounded-2xl p-4 md:p-5"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <h4 className="course-detail-display text-xl font-bold text-white">
                Cheatsheet Preview
              </h4>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsPreviewModalOpen(false)}
              >
                Close
              </Button>
            </div>
            <img
              src={cheatsheetPreviewUrl}
              alt={`${course.title} full cheatsheet preview`}
              className="max-h-[80vh] w-full rounded-xl object-contain"
            />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};
